import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, finalize } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AdminUserModel } from '../../../pagesAdmin/admin-users/admin-user.model';
import { AdminUserService } from '../../user/admin-user.service';

const API_URL = environment.apiUrl + '/admin/auth';

interface LoginResponse {
  token: string;
  admin: AdminUserModel;
}

interface RefreshResponse {
  token: string;
  admin?: AdminUserModel;
}

@Injectable()
export class AuthAdminService {
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private adminUserService: AdminUserService
  ) {}

  login(email: string, password: string): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<LoginResponse>(`${API_URL}/login`, { email, password })
      .pipe(
        tap((res) => {
          if (res && res.token) {
            this.adminUserService.setToken(res.token);
          }
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  logout() {
    this.adminUserService.logout();
    this.router.navigate(['/auth-admin/login']);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${API_URL}/forgot-password`, { email });
  }

  resetPassword(token: string, password: string, confirmPassword: string): Observable<any> {
    return this.http.post(`${API_URL}/reset-password`, {
      token,
      password,
      confirmPassword
    });
  }

  validateResetToken(token: string): Observable<any> {
    return this.http.post(`${API_URL}/validate-reset-token`, { token });
  }

  refreshToken(): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(`${API_URL}/refresh`, {})
      .pipe(
        tap((res) => {
          if (res && res.token) {
            this.adminUserService.setToken(res.token);
          }
        })
      );
  }

  isAuthenticated(): boolean {
    return this.adminUserService.isLogged() && !this.adminUserService.isTokenExpired();
  }

  getCurrentAdmin(): Observable<AdminUserModel> {
    return this.adminUserService.getUser();
  }

  updateProfile(data: any): Observable<AdminUserModel> {
    return this.http.put<AdminUserModel>(`${environment.apiUrl}/admin/profile`, data);
  }

  changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Observable<any>;
  changePassword(body: any, userId: number): Observable<any>;
  changePassword(currentPasswordOrBody: any, newPasswordOrUserId?: any, confirmPassword?: string): Observable<any> {
    // New signature
    if (typeof currentPasswordOrBody === 'string' && typeof newPasswordOrUserId === 'string') {
      return this.http.put(`${environment.apiUrl}/admin/change-password`, {
        CurrentPassword: currentPasswordOrBody,
        NewPassword: newPasswordOrUserId,
        ConfirmPassword: confirmPassword
      });
    }

    // Legacy signature
    return this.http
      .put(`${environment.apiUrl}/admin/change-password`, currentPasswordOrBody, {
        observe: 'response',
      })
      .pipe(
        tap((res: any) => {
          const authToken = res.body.token;
          this.adminUserService.setToken(authToken);
        })
      );
  }

  getProfile(): Observable<AdminUserModel> {
    return this.http.get<AdminUserModel>(`${environment.apiUrl}/admin/profile`);
  }

  // Legacy methods for backward compatibility
  authenticate(login: any) {
    return this.login(login.email, login.password);
  }

  forgotPasswordVerifyToken(token: string) {
    return this.validateResetToken(token);
  }

  forgotPasswordReset(body: any) {
    return this.resetPassword(body.token, body.password, body.confirmPassword);
  }

  editUser(body: any, userId: number) {
    return this.updateProfile(body);
  }
}
